import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowRight, Loader2, Plus, Save } from "lucide-react";
import { fetchCvrDenmarkRaw, mapCVRToFacilityFields } from "../../../apis/cvrDenmark";
import { fetchViesVat } from "../../../apis/euVies";
import {
  ALL_COUNTRIES,
  BUILDING_TYPES_EN,
  DEFAULT_ACCOUNTING_PERIODS,
  EU_COUNTRIES,
  OWNERSHIP_OPTIONS_EN,
} from "./addFacilityConstants";
import {
  buildAddressLine1,
  buildCheckedTypesForSingleBuilding,
  defaultOwnershipPercent,
  mapOwnershipToApi,
  parseAccountingPeriodToApiDates,
  parseWholeNumber,
} from "./addFacilityMappers";
import "./NewFacilityLovable.css";

const BUILDING_TYPES = BUILDING_TYPES_EN;
const OWNERSHIP_OPTIONS = OWNERSHIP_OPTIONS_EN;

const emptyForm = () => ({
  name: "",
  street: "",
  postalCode: "",
  city: "",
  country: "",
  vat: "",
  buildingType: "",
  area: "",
  ownership: "",
  fte: "",
  revenue: "",
  accountingPeriod: "",
  baseline: false,
});

const fromBadgeStyle = {
  position: "absolute",
  top: 4,
  right: 6,
  fontSize: 9,
  fontWeight: 500,
  padding: "1px 6px",
  borderRadius: 20,
  background: "rgba(15,110,86,0.08)",
  color: "#085041",
  border: "0.5px solid rgba(15,110,86,0.20)",
};

function FromBadge({ source }) {
  return <span style={fromBadgeStyle}>Fra {source}</span>;
}

function Required() {
  return (
    <div style={{ fontSize: 10, color: "#A32D2D", marginTop: 2 }}>Påkrævet</div>
  );
}

function ManualField({ label, value, onChange }) {
  return (
    <div>
      <label className="nfl-label">{label}</label>
      <input
        className="nfl-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function FormField({ label, children, required, badgeSource }) {
  return (
    <div>
      <div className="nfl-fieldLabel">
        {label}
        {required ? <span style={{ color: "#A32D2D" }}> *</span> : null}
      </div>
      <div className="nfl-fieldInputWrap">
        {children}
        {badgeSource ? <FromBadge source={badgeSource} /> : null}
      </div>
    </div>
  );
}

const pad = (n) => String(n).padStart(2, "0");
const fmt = (d) =>
  `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${String(d.getFullYear()).slice(-2)}`;

export default function NewFacilityLovable({ onSave, onCancel }) {
  const [stage, setStage] = useState("lookup");
  const [mode, setMode] = useState("dk");

  const [cvr, setCvr] = useState("");
  const [dkStatus, setDkStatus] = useState("idle");
  const [dkMsg, setDkMsg] = useState("");

  const [euCode, setEuCode] = useState("SE");
  const [euVat, setEuVat] = useState("");
  const [euStatus, setEuStatus] = useState("idle");

  const [form, setForm] = useState(emptyForm);
  const [autoFlags, setAutoFlags] = useState({});
  const [autoSource, setAutoSource] = useState(null);

  const [periods, setPeriods] = useState(() => [...DEFAULT_ACCOUNTING_PERIODS]);
  const [showAddPeriod, setShowAddPeriod] = useState(false);
  const [pStart, setPStart] = useState("");
  const [pEnd, setPEnd] = useState("");

  const [showErrors, setShowErrors] = useState(false);

  const set = useCallback((k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setAutoFlags((a) => {
      if (!a[k]) return a;
      return { ...a, [k]: false };
    });
  }, []);

  const applyDk = useCallback((data, digits) => {
    if (!data || data.error) {
      setDkStatus("notfound");
      setDkMsg("CVR ikke fundet — udfyld manuelt");
      return;
    }
    if (data.enddate) {
      setDkStatus("dissolved");
      setDkMsg(
        `Denne virksomhed er ophørt ${data.enddate} — er du sikker på at du vil fortsætte?`
      );
    } else {
      setDkStatus("found");
      setDkMsg(`Fundet: ${data.name}`);
    }
    const m = mapCVRToFacilityFields(data);
    setForm((f) => ({
      ...f,
      name: m.name || f.name,
      street: m.street || f.street,
      postalCode: m.postal_code || f.postalCode,
      city: m.city || f.city,
      country: m.country || "Danmark",
      vat: m.vat || digits,
    }));
    setAutoFlags({
      name: true,
      street: true,
      postalCode: true,
      city: true,
      country: true,
      vat: true,
    });
    setAutoSource("CVR");
  }, []);

  useEffect(() => {
    if (mode !== "dk") return;
    if (cvr.length !== 8) {
      setDkStatus("idle");
      setDkMsg("");
      return;
    }
    let cancelled = false;
    setDkStatus("loading");
    setDkMsg("");
    fetchCvrDenmarkRaw(cvr)
      .then((data) => {
        if (cancelled) return;
        if (data == null) {
          setDkStatus("error");
          setDkMsg(
            "Kunne ikke hente data — tjek din forbindelse eller udfyld manuelt"
          );
          return;
        }
        applyDk(data, cvr);
      })
      .catch(() => {
        if (cancelled) return;
        setDkStatus("error");
        setDkMsg(
          "Kunne ikke hente data — tjek din forbindelse eller udfyld manuelt"
        );
      });
    return () => {
      cancelled = true;
    };
  }, [cvr, mode, applyDk]);

  const applyEu = useCallback(
    (data, countryName) => {
      const isValid = data?.isValid === true;
      setEuStatus(isValid ? "valid" : "invalid");
      const name = data?.name && data.name !== "---" ? data.name : "";
      const address =
        data?.address && data.address !== "---" ? data.address : "";
      setForm((f) => ({
        ...f,
        name: name || f.name,
        street: address || f.street,
        country: countryName || f.country,
        vat: `${euCode}${euVat}`,
      }));
      setAutoFlags({
        name: !!name,
        street: !!address,
        country: true,
        vat: true,
      });
      setAutoSource("VIES");
    },
    [euCode, euVat]
  );

  const runEuLookup = useCallback(() => {
    if (!euVat.trim()) {
      setEuStatus("idle");
      return;
    }
    const country =
      EU_COUNTRIES.find((c) => c.code === euCode)?.name ?? "";
    setEuStatus("loading");
    fetchViesVat(euCode, euVat)
      .then((data) => {
        applyEu(data, country);
      })
      .catch(() => {
        setEuStatus("error");
      });
  }, [euCode, euVat, applyEu]);

  const continueToForm = () => {
    setForm((f) => {
      if (f.vat.trim()) return f;
      if (mode === "dk" && cvr) {
        return { ...f, vat: cvr, country: f.country || "Danmark" };
      }
      if (mode === "eu" && euVat) {
        const country =
          EU_COUNTRIES.find((c) => c.code === euCode)?.name ?? f.country;
        return { ...f, vat: `${euCode}${euVat}`, country: f.country || country };
      }
      return f;
    });
    setAutoFlags((a) => ({ ...a, vat: true, country: a.country ?? true }));
    setStage("form");
  };

  const fteNumber = parseWholeNumber(form.fte);
  const areaNumber = parseWholeNumber(form.area);
  const revenueNumber = parseWholeNumber(form.revenue);

  const requiredMissing = useMemo(
    () => ({
      name: !form.name.trim(),
      vat: !form.vat.trim(),
      accountingPeriod: !form.accountingPeriod.trim(),
      fte: fteNumber === null,
      area: String(form.area || "").trim() !== "" && areaNumber === null,
      revenue: String(form.revenue || "").trim() !== "" && revenueNumber === null,
    }),
    [
      form.name,
      form.vat,
      form.accountingPeriod,
      form.fte,
      form.area,
      form.revenue,
      fteNumber,
      areaNumber,
      revenueNumber,
    ]
  );

  const reqBorder = (k) =>
    showErrors && requiredMissing[k]
      ? { borderColor: "#A32D2D", borderWidth: 0.5 }
      : {};

  const addPeriod = () => {
    if (!pStart || !pEnd) return;
    const s = new Date(pStart);
    const e = new Date(pEnd);
    const label = `${fmt(s)} - ${fmt(e)} (${e.getFullYear()})`;
    setPeriods((arr) => [...arr, label]);
    setForm((f) => ({ ...f, accountingPeriod: label }));
    setShowAddPeriod(false);
    setPStart("");
    setPEnd("");
  };

  const save = () => {
    if (
      requiredMissing.name ||
      requiredMissing.vat ||
      requiredMissing.accountingPeriod ||
      requiredMissing.fte ||
      requiredMissing.area ||
      requiredMissing.revenue
    ) {
      setShowErrors(true);
      return;
    }
    const dates = parseAccountingPeriodToApiDates(form.accountingPeriod);
    const ownershipApi = mapOwnershipToApi(form.ownership);
    const ownershipPerc = defaultOwnershipPercent(form.ownership);
    const checked = buildCheckedTypesForSingleBuilding(form.buildingType, (k) => k);
    const typesString = checked
      .filter((row) => row.checked)
      .map((row) => row.name_en)
      .join(",");
    const space_type = checked
      .filter((row) => row.checked)
      .map((row) => ({
        type: row.name_en,
        percentage:
          row.space_percentage == null ? null : Number(row.space_percentage),
      }));
    const address1 = buildAddressLine1({
      street: form.street,
      city: form.city,
      postalCode: form.postalCode,
      country: form.country,
    });
    const totalSpaceNum =
      String(form.area || "").trim() === ""
        ? undefined
        : Number(String(form.area).trim());

    onSave?.({
      name: String(form.name || "").trim(),
      street: form.street,
      postalCode: form.postalCode,
      city: form.city,
      country: form.country,
      vat: String(form.vat || "").trim(),
      buildingType: form.buildingType,
      area: form.area,
      ownership: form.ownership,
      fte: form.fte,
      revenue: form.revenue,
      accountingPeriod: form.accountingPeriod,
      baseline: Boolean(form.baseline),
      address1,
      address2: "",
      yearStart: dates?.yearStart ?? "",
      yearEnd: dates?.yearEnd ?? "",
      typesString,
      space_type,
      ownershipApi,
      ownershipPerc,
      total_space:
        totalSpaceNum !== undefined && !Number.isNaN(totalSpaceNum)
          ? totalSpaceNum
          : undefined,
      vat_num: String(form.vat || "").trim(),
    });
  };

  const continueDisabled = mode === "dk" && dkStatus === "idle" && !cvr;

  return (
    <div className="nfl-overlay" onClick={() => onCancel?.()} role="presentation">
      <div
        className="nfl-dialog"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="nfl-kicker">Facility</div>
        <div className="nfl-title">{form.name || "New facility"}</div>

        {stage === "lookup" ? (
          <div className="nfl-stack">
            <div>
              <div className="nfl-kicker" style={{ marginBottom: "0.5rem" }}>
                Virksomhedstype
              </div>
              <div className="nfl-typeGrid">
                {[
                  { k: "dk", flag: "🇩🇰", label: "Dansk", sub: "CVR-opslag" },
                  { k: "eu", flag: "🇪🇺", label: "EU", sub: "VIES-opslag" },
                  { k: "other", flag: "🌍", label: "Øvrige", sub: "Manuelt" },
                ].map((opt) => {
                  const active = mode === opt.k;
                  return (
                    <button
                      key={opt.k}
                      type="button"
                      className={
                        "nfl-typeBtn" + (active ? " nfl-typeBtn--active" : "")
                      }
                      onClick={() => setMode(opt.k)}
                    >
                      <div className="nfl-typeBtn__main">
                        {opt.flag} {opt.label}
                      </div>
                      <div className="nfl-typeBtn__sub">{opt.sub}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {mode === "dk" ? (
              <div className="nfl-stack" style={{ gap: "0.5rem" }}>
                <label className="nfl-label">CVR-nummer</label>
                <input
                  className="nfl-input"
                  inputMode="numeric"
                  maxLength={8}
                  placeholder="8 cifre — fx 40003002"
                  value={cvr}
                  onChange={(e) => setCvr(e.target.value.replace(/\D/g, ""))}
                />
                {dkStatus === "loading" ? (
                  <div className="nfl-inlineHint nfl-muted">
                    <Loader2 className="nfl-spin" style={{ width: 12, height: 12 }} />
                    Henter virksomhedsdata...
                  </div>
                ) : null}
                {dkStatus === "found" ? (
                  <div className="text-xs" style={{ color: "#085041" }}>
                    ✓ {dkMsg}
                  </div>
                ) : null}
                {(dkStatus === "notfound" || dkStatus === "error") && dkMsg ? (
                  <div className="text-xs" style={{ color: "#BA7517" }}>
                    {dkMsg}
                  </div>
                ) : null}
                {dkStatus === "dissolved" ? (
                  <div className="text-xs" style={{ color: "#BA7517" }}>
                    {dkMsg}
                  </div>
                ) : null}
              </div>
            ) : null}

            {mode === "eu" ? (
              <div className="nfl-stack" style={{ gap: "0.5rem" }}>
                <label className="nfl-label">VAT-nummer</label>
                <div className="nfl-row">
                  <select
                    className="nfl-input nfl-input--narrow"
                    value={euCode}
                    onChange={(e) => setEuCode(e.target.value)}
                  >
                    {EU_COUNTRIES.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.code}
                      </option>
                    ))}
                  </select>
                  <input
                    className="nfl-input"
                  placeholder="e.g. 12345678"
                    value={euVat}
                    onChange={(e) =>
                      setEuVat(e.target.value.toUpperCase().replace(/\s/g, ""))
                    }
                    onBlur={runEuLookup}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") runEuLookup();
                    }}
                  />
                </div>
                {euStatus === "loading" ? (
                  <div className="nfl-inlineHint nfl-muted">
                    <Loader2 className="nfl-spin" style={{ width: 12, height: 12 }} />
                    Henter virksomhedsdata...
                  </div>
                ) : null}
                {euStatus === "valid" ? (
                  <div className="text-xs nfl-muted">
                    Navn og adresse hentes automatisk for de fleste EU-lande.
                  </div>
                ) : null}
                {euStatus === "invalid" ? (
                  <div className="text-xs" style={{ color: "#BA7517" }}>
                    VAT-nummer ikke gyldigt i VIES-registret
                  </div>
                ) : null}
                {euStatus === "error" ? (
                  <div className="text-xs" style={{ color: "#BA7517" }}>
                    VIES-registret er ikke tilgængeligt — udfyld manuelt
                  </div>
                ) : null}
              </div>
            ) : null}

            {mode === "other" ? (
              <div className="nfl-stack">
                <div className="text-xs nfl-muted">
                  Ingen automatisk opslag — udfyld oplysningerne manuelt.
                </div>
                <ManualField
                  label="Virksomhedsnavn"
                  value={form.name}
                  onChange={(v) => set("name", v)}
                />
                <ManualField
                  label="VAT / Registreringsnummer"
                  value={form.vat}
                  onChange={(v) => set("vat", v)}
                />
                <div>
                  <label className="nfl-label">Land</label>
                  <select
                    className="nfl-input"
                    value={form.country}
                    onChange={(e) => set("country", e.target.value)}
                  >
                    <option value="">Vælg land…</option>
                    {ALL_COUNTRIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
                <ManualField
                  label="Adresse"
                  value={form.street}
                  onChange={(v) => set("street", v)}
                />
                <div className="nfl-formGrid3">
                  <ManualField
                    label="Postnr."
                    value={form.postalCode}
                    onChange={(v) => set("postalCode", v)}
                  />
                  <div className="nfl-span2">
                    <ManualField
                      label="By"
                      value={form.city}
                      onChange={(v) => set("city", v)}
                    />
                  </div>
                </div>
              </div>
            ) : null}

            <div className="nfl-foot">
              <button
                type="button"
                className="nfl-btnPrimary"
                disabled={continueDisabled}
                onClick={continueToForm}
              >
                Fortsæt til facility <ArrowRight style={{ width: 16, height: 16 }} />
              </button>
            </div>
          </div>
        ) : (
          <div className="nfl-stack">
            <FormField
              label="Facility name"
              required={showErrors && requiredMissing.name}
              badgeSource={autoFlags.name ? autoSource : null}
            >
              <input
                className="nfl-input"
                style={reqBorder("name")}
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
              />
              {showErrors && requiredMissing.name ? <Required /> : null}
            </FormField>

            <FormField
              label="Street and number"
              badgeSource={autoFlags.street ? autoSource : null}
            >
              <input
                className="nfl-input"
                value={form.street}
                onChange={(e) => set("street", e.target.value)}
              />
            </FormField>

            <div className="nfl-formGrid3">
              <FormField
                label="Postal code"
                badgeSource={autoFlags.postalCode ? autoSource : null}
              >
                <input
                  className="nfl-input"
                  value={form.postalCode}
                  onChange={(e) => set("postalCode", e.target.value)}
                  placeholder="e.g. 2930"
                  inputMode="numeric"
                />
              </FormField>
              <div className="nfl-span2">
                <FormField
                  label="City"
                  badgeSource={autoFlags.city ? autoSource : null}
                >
                  <input
                    className="nfl-input"
                    value={form.city}
                    onChange={(e) => set("city", e.target.value)}
                  />
                </FormField>
              </div>
            </div>

            <div className="nfl-formGrid2">
              <FormField
                label="Country"
                badgeSource={autoFlags.country ? autoSource : null}
              >
                <select
                  className="nfl-input"
                  value={form.country}
                  onChange={(e) => set("country", e.target.value)}
                >
                  <option value="">Vælg land…</option>
                  {ALL_COUNTRIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField
                label="VAT no."
                required={showErrors && requiredMissing.vat}
                badgeSource={autoFlags.vat ? autoSource : null}
              >
                <input
                  className="nfl-input"
                  style={reqBorder("vat")}
                  value={form.vat}
                  onChange={(e) => set("vat", e.target.value)}
                  placeholder="e.g. 40003002"
                />
                {showErrors && requiredMissing.vat ? <Required /> : null}
              </FormField>
            </div>

            <div className="nfl-formGrid2">
              <FormField label="Building type">
                <select
                  className="nfl-input"
                  value={form.buildingType}
                  onChange={(e) => set("buildingType", e.target.value)}
                >
                  <option value="">Vælg…</option>
                  {BUILDING_TYPES.map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label="Area">
                <div className="nfl-areaRow">
                  <input
                    className="nfl-input"
                    style={reqBorder("area")}
                    value={form.area}
                    onChange={(e) => set("area", e.target.value)}
                    placeholder="e.g. 130"
                    inputMode="numeric"
                  />
                  <span className="text-sm nfl-muted">m²</span>
                </div>
                {showErrors && requiredMissing.area ? (
                  <div style={{ fontSize: 10, color: "#A32D2D", marginTop: 2 }}>
                    Enter a whole number of m²
                  </div>
                ) : null}
              </FormField>
            </div>

            <div className="nfl-formGrid2">
              <FormField label="Ownership">
                <select
                  className="nfl-input"
                  value={form.ownership}
                  onChange={(e) => set("ownership", e.target.value)}
                >
                  <option value="">Vælg…</option>
                  {OWNERSHIP_OPTIONS.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField
                label="FTE"
                required={showErrors && requiredMissing.fte}
              >
                <input
                  type="number"
                  min="0"
                  step="1"
                  className="nfl-input"
                  style={reqBorder("fte")}
                  value={form.fte}
                  onChange={(e) => set("fte", e.target.value)}
                  placeholder="e.g. 8"
                />
                {showErrors && requiredMissing.fte ? (
                  <Required />
                ) : (
                  <div style={{ fontSize: 10, color: "#6b7280", marginTop: 2 }}>
                    Whole number of full-time employees
                  </div>
                )}
              </FormField>
            </div>

            <div className="nfl-formGrid2">
              <FormField label="Revenue (DKK)">
                <input
                  type="number"
                  min="0"
                  step="1"
                  className="nfl-input"
                  style={reqBorder("revenue")}
                  value={form.revenue}
                  onChange={(e) => set("revenue", e.target.value)}
                  placeholder="e.g. 2000000"
                />
                {showErrors && requiredMissing.revenue ? (
                  <div style={{ fontSize: 10, color: "#A32D2D", marginTop: 2 }}>
                    Enter a whole number, no decimals or currency symbols
                  </div>
                ) : (
                  <div style={{ fontSize: 10, color: "#6b7280", marginTop: 2 }}>
                    Whole DKK amount, e.g. 2000000
                  </div>
                )}
              </FormField>
              <FormField
                label="Accounting period"
                required={showErrors && requiredMissing.accountingPeriod}
              >
                <select
                  className="nfl-input"
                  style={reqBorder("accountingPeriod")}
                  value={form.accountingPeriod}
                  onChange={(e) => {
                    if (e.target.value === "__add__") {
                      setShowAddPeriod(true);
                      return;
                    }
                    set("accountingPeriod", e.target.value);
                  }}
                >
                  <option value="">Vælg…</option>
                  {periods.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                  <option value="__add__">+ Tilføj ny periode</option>
                </select>
                {showAddPeriod ? (
                  <div className="nfl-addPeriodRow">
                    <input
                      type="date"
                      className="nfl-input"
                      style={{ maxWidth: 140 }}
                      value={pStart}
                      onChange={(e) => setPStart(e.target.value)}
                    />
                    <input
                      type="date"
                      className="nfl-input"
                      style={{ maxWidth: 140 }}
                      value={pEnd}
                      onChange={(e) => setPEnd(e.target.value)}
                    />
                    <button type="button" className="nfl-btnTiny" onClick={addPeriod}>
                      <Plus style={{ width: 12, height: 12 }} />
                    </button>
                  </div>
                ) : null}
                {showErrors && requiredMissing.accountingPeriod ? (
                  <Required />
                ) : null}
              </FormField>
            </div>

            <FormField label="Baseline">
              <label
                className="inline-flex items-center gap-2 text-sm"
                style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
              >
                <input
                  type="checkbox"
                  checked={form.baseline}
                  onChange={(e) => set("baseline", e.target.checked)}
                />
                {form.baseline ? "Yes" : "No"}
              </label>
            </FormField>

            <div className="nfl-foot nfl-foot--border">
              <button type="button" className="nfl-btnPrimary" onClick={save}>
                <Save style={{ width: 16, height: 16 }} /> Save
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
